import React, { useEffect, useMemo } from 'react';
import { Stack, Snackbar, AlertTitle } from '@mui/material';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import styled from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectSchemaDialogErrors, selectSchemaDialogOpen } from '@/redux/selectors/layoutSelectors';
import { clearSchemaDialogErrors } from '@/redux/slices/layoutSlice';

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} {...props} />;
});

export default function SchemaFormErrorsDialog() {
  const dispatch = useAppDispatch();
  const open = useAppSelector(selectSchemaDialogOpen);
  const errors = useAppSelector(selectSchemaDialogErrors);

  useEffect(() => {
    return () => {
      dispatch(clearSchemaDialogErrors());
    };
  }, []);

  const errorLabels = useMemo(() => {
    // grab only labels to display
    const labels = errors.map((error) => {
      const match = error.stack.match(/'([^']*)'/);
      return match?.[1];
    });
    // remove duplicates
    return labels?.filter((label, index) => labels.indexOf(label) === index);
  }, [errors]);

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    dispatch(clearSchemaDialogErrors());
  };

  if (!open) return null;
  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <Snackbar open={open} autoHideDuration={6000} onClose={handleClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleClose} severity="error" sx={{ width: '100%' }} onClick={(e) => e.stopPropagation()}>
          <div>
            {errorLabels?.length && (
              <div>
                <StyledAlertTitle>Invalid/Missing Fields</StyledAlertTitle>
                <ul>{errorLabels?.map((label) => <li key={label}>{label}</li>)}</ul>
              </div>
            )}
          </div>
        </Alert>
      </Snackbar>
    </Stack>
  );
}

const StyledAlertTitle = styled(AlertTitle)`
  font-size: 1.1rem;
  font-weight: bold;
  + ul {
    padding-left: 15px;
  }
`;
